import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Inbox, DollarSign, MessageSquare, RefreshCw } from "lucide-react";
import { api, getAdminToken, setAdminToken } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = getAdminToken();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [consults, setConsults] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/consultations"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setConsults(c.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminToken(null);
        toast.error("Session expired");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="bg-stone-950 min-h-[80vh] py-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="label-eyebrow mb-2">Admin</div>
            <h1 className="font-display text-4xl font-black text-stone-50 tracking-tight">
              Mission Control
            </h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="bg-transparent border-stone-700 text-stone-200 hover:bg-stone-900 rounded-none"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              variant="outline"
              data-testid="admin-logout-btn"
              onClick={() => {
                setAdminToken(null);
                navigate("/admin/login");
              }}
              className="bg-transparent border-stone-700 text-stone-200 hover:bg-stone-900 rounded-none"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard
            icon={Inbox}
            label="Total Bookings"
            value={stats?.total_bookings ?? "—"}
            testid="stat-bookings"
          />
          <StatCard
            icon={DollarSign}
            label="Paid Deposits"
            value={stats?.paid_deposits ?? "—"}
            testid="stat-paid"
          />
          <StatCard
            icon={MessageSquare}
            label="Consultations"
            value={stats?.consultation_requests ?? "—"}
            testid="stat-consults"
          />
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-stone-900 border border-stone-800 rounded-none p-1">
            <TabsTrigger
              value="bookings"
              data-testid="admin-tab-bookings"
              className="rounded-none data-[state=active]:bg-yellow-500 data-[state=active]:text-yellow-950"
            >
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger
              value="consults"
              data-testid="admin-tab-consults"
              className="rounded-none data-[state=active]:bg-yellow-500 data-[state=active]:text-yellow-950"
            >
              Consultations ({consults.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="border border-stone-800 bg-stone-900 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-800 hover:bg-stone-900">
                    <TableHead className="text-stone-400">When</TableHead>
                    <TableHead className="text-stone-400">Name</TableHead>
                    <TableHead className="text-stone-400">Service</TableHead>
                    <TableHead className="text-stone-400">Date</TableHead>
                    <TableHead className="text-stone-400">Contact</TableHead>
                    <TableHead className="text-stone-400">Address</TableHead>
                    <TableHead className="text-stone-400">Payment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-stone-500 py-8 text-center">No bookings yet.</TableCell></TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.id} className="border-stone-800 hover:bg-stone-950/60" data-testid={`booking-row-${b.id}`}>
                      <TableCell className="text-stone-300 text-xs whitespace-nowrap">
                        {new Date(b.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-stone-100 font-semibold">{b.full_name}</TableCell>
                      <TableCell className="text-stone-300">{b.service}</TableCell>
                      <TableCell className="text-stone-300 text-sm">
                        {b.preferred_date || "—"}
                        {b.preferred_window ? ` · ${b.preferred_window}` : ""}
                      </TableCell>
                      <TableCell className="text-stone-300 text-xs">
                        <div>{b.email}</div>
                        <div>{b.phone}</div>
                      </TableCell>
                      <TableCell className="text-stone-400 text-xs max-w-[200px]">{b.address}</TableCell>
                      <TableCell>
                        <PaymentBadge status={b.payment_status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="consults" className="mt-6">
            <div className="border border-stone-800 bg-stone-900 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-800">
                    <TableHead className="text-stone-400">When</TableHead>
                    <TableHead className="text-stone-400">Name</TableHead>
                    <TableHead className="text-stone-400">Contact</TableHead>
                    <TableHead className="text-stone-400">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consults.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-stone-500 py-8 text-center">No consultations yet.</TableCell></TableRow>
                  )}
                  {consults.map((c) => (
                    <TableRow key={c.id} className="border-stone-800">
                      <TableCell className="text-stone-300 text-xs">{new Date(c.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-stone-100 font-semibold">{c.full_name}</TableCell>
                      <TableCell className="text-stone-300 text-xs">
                        <div>{c.email}</div>
                        <div>{c.phone || "—"}</div>
                      </TableCell>
                      <TableCell className="text-stone-300 max-w-md">{c.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-stone-800 bg-stone-900 p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        <Icon className="h-5 w-5 text-yellow-400" />
      </div>
      <div className="mt-3 font-display text-4xl font-black text-stone-50">{value}</div>
    </div>
  );
}

function PaymentBadge({ status }) {
  const styles = {
    paid: "bg-yellow-500 text-yellow-950",
    pending: "bg-stone-700 text-stone-100",
    none: "bg-stone-800 text-stone-400",
    failed: "bg-red-600 text-white",
    canceled: "bg-stone-700 text-stone-300",
  };
  return (
    <Badge className={`rounded-none uppercase tracking-wider text-[10px] ${styles[status] || styles.none}`}>
      {status || "none"}
    </Badge>
  );
}
